import { FormattedMessage, useIntl } from 'react-intl';
import { FormSection } from '../FormSection';
import { Loading } from '../Loading';
import { useMemo, useCallback, ReactNode, useState } from 'react';
import {
  DepartmentAccessLevelDTO,
  getGetCurrentDepartmentTreeQueryKey,
  useGetCurrentDepartmentTree,
  useGetDepartmentUserWithDepartments,
  getGetDepartmentUserWithDepartmentsQueryKey,
  useUpdateDepartmentUser,
  useDeleteDepartmentUser,
  getGetUsersForDepartmentQueryKey,
  useDeleteUserProfileByID,
  getListOrganizationUsersQueryKey,
  getGetDepartmentUsersQueryKey,
  useUpdateOrganizationUserRole,
  useInviteUserToDepartment,
  useGetCurrentOrganization,
} from '@/api/trafikportalen';
import { useTenantID } from '@/hooks/useTenantID';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/useToast';
import { getAllUserDepartmentAccess } from '@/utils/departmentAccess';
import { findDepartmentByCollectionType } from '@/hooks/useGetStudentCollection';
import { DepartmentTree } from '../Departments/DepartmentTree';
import {
  IconCheckCircle,
  IconEdit,
  IconEye,
  IconPlus,
  IconSmartcard,
  IconTrash,
  IconXMarkCircle,
} from '@/icons';
import {
  hasDepartmentAccess,
  hasDepartmentWriteAccess,
  isDepartmentUser,
  getWriteAccessDepartmentIds,
} from '@/helpers/departmentPermissions';
import IconButton from '../core/IconButton';
import { WarningDialog } from '../WarningDialog';
import { ExtendedUserDTO } from '@/hooks/useGetAllAdmins';
import { cn } from '@/helpers/classnames';
import { useAdministratorTranslations } from '@/hooks/useAdministratorTranslations';
import { useRoleIds, useStudentsDisabled } from '@/contexts/ConfigContext';
import {
  adminTypeColor,
  isOrganizationRole,
  type AdminType,
} from '@/types/administrator';
import { checkUserPermissions } from '../Auth/checkUserPermissions';
import { refreshDepartmentAccess, useAuth } from '@/stores/auth';
import Button from '../core/Button';
import { AccessLevelDescription, AccessLevelToggle } from './AccessLevelToggle';
import type { SelectedDepartment } from '@/types/selectedDepartment';
import { FormElement } from '../FormElement';
import {
  COLLECTION_TYPE_LABELS,
  STUDENT_COLLECTION_TYPES,
  type StudentCollectionType,
} from '@/helpers/collectionTypes';

type GrantedAccessLevel = Exclude<DepartmentAccessLevelDTO, 'none'>;

const organizationAccessLevel: Partial<Record<AdminType, GrantedAccessLevel>> =
  {
    orgAdmin: 'editor',
    orgBearerManager: 'bearer-manager',
    orgReader: 'viewer',
  };

const AccessLevelChip = ({
  accessLevel,
}: {
  accessLevel: DepartmentAccessLevelDTO;
}) => {
  const base = 'flex w-fit items-center gap-1 rounded-full px-3 py-1';

  if (accessLevel === DepartmentAccessLevelDTO.editor) {
    return (
      <div className={cn(base, adminTypeColor.deptAdmin)}>
        <IconEdit className="size-4" aria-hidden="true" />
        <FormattedMessage defaultMessage="Redigering" />
      </div>
    );
  }

  if (accessLevel === DepartmentAccessLevelDTO['bearer-manager']) {
    return (
      <div className={cn(base, adminTypeColor.deptBearerManager)}>
        <IconSmartcard className="size-4" aria-hidden="true" />
        <FormattedMessage defaultMessage="Bärarhantering" />
      </div>
    );
  }

  if (accessLevel === DepartmentAccessLevelDTO.viewer) {
    return (
      <div className={cn(base, adminTypeColor.deptReader)}>
        <IconEye className="size-4" aria-hidden="true" />
        <FormattedMessage defaultMessage="Läs" />
      </div>
    );
  }

  return (
    <div className={cn(base, 'bg-background-300 text-text-700')}>
      <IconXMarkCircle className="size-4" aria-hidden="true" />
      <FormattedMessage defaultMessage="Ingen" />
    </div>
  );
};

interface Props {
  administrator: ExtendedUserDTO;
}

export const AdministratorAccess = ({ administrator }: Props) => {
  const tenantId = useTenantID();
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const getAdminTypeTranslation = useAdministratorTranslations();
  const { userProfile, departmentAccess } = useAuth();
  const roleIds = useRoleIds();
  const studentsDisabled = useStudentsDisabled();

  const { data: userWithDepartments, isLoading: isLoadingDepartments } =
    useGetDepartmentUserWithDepartments(administrator.id);
  const { data: departmentTree } = useGetCurrentDepartmentTree();
  const { data: organization } = useGetCurrentOrganization();

  const isEditingSelf = administrator.email === userProfile?.email;
  const organizationLevel = organizationAccessLevel[administrator.adminType];

  const updateOrganizationUserRole = useUpdateOrganizationUserRole();
  const updateDepartmentUser = useUpdateDepartmentUser();
  const deleteDepartmentUser = useDeleteDepartmentUser();
  const deleteOrgAdmin = useDeleteUserProfileByID();
  const inviteUserToDepartment = useInviteUserToDepartment();

  const [showDeleteOrgAdmin, setShowDeleteOrgAdmin] = useState<boolean>(false);
  const [deleteDepartmentUserId, setDeleteDepartmentUserId] =
    useState<string>('');
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [selectedNewDepartmentIds, setSelectedNewDepartmentIds] = useState<
    string[]
  >([]);
  const [newDepartmentsAccessLevel, setNewDepartmentsAccessLevel] =
    useState<GrantedAccessLevel>(DepartmentAccessLevelDTO.editor);
  const [pendingOrganizationLevel, setPendingOrganizationLevel] =
    useState<GrantedAccessLevel>();
  const [pendingDepartmentChange, setPendingDepartmentChange] = useState<{
    departmentId: string;
    value: GrantedAccessLevel;
  } | null>(null);
  const [pendingCategoryChange, setPendingCategoryChange] = useState<{
    departmentId: string;
    value: DepartmentAccessLevelDTO;
  } | null>(null);

  const adminTypeText = useMemo(() => {
    switch (administrator.adminType) {
      case 'orgAdmin':
        return formatMessage({
          defaultMessage:
            'Kan komma åt och redigera i alla avdelningar i organisationen.',
        });
      case 'orgBearerManager':
        return formatMessage({
          defaultMessage:
            'Läsåtkomst med bärarhantering i alla avdelningar i organisationen: kan se information och byta kort och bärare på resenärer och biljetter, men inte redigera något annat.',
        });
      case 'orgReader':
        return formatMessage({
          defaultMessage:
            'Kan komma åt information i alla avdelningar i organisationen, men inte redigera.',
        });
      default:
        return '';
    }
  }, [administrator.adminType, formatMessage]);

  const allDepartmentAccess = useMemo(() => {
    if (!userWithDepartments?.departments || !departmentTree) {
      return [];
    }
    return getAllUserDepartmentAccess(
      userWithDepartments.departments,
      departmentTree,
    );
  }, [userWithDepartments?.departments, departmentTree]);

  const studentDepartmentIds = useMemo(() => {
    if (!departmentTree) return new Set<string>();
    const ids = new Set<string>();
    for (const type of STUDENT_COLLECTION_TYPES) {
      const dept = findDepartmentByCollectionType(departmentTree, type);
      if (dept) ids.add(dept.id);
    }
    return ids;
  }, [departmentTree]);

  const hasNonStudentDepartmentAccess = useMemo(() => {
    if (!departmentAccess?.departments) return false;
    return Object.entries(departmentAccess.departments).some(
      ([id, info]) =>
        info.access_level !== 'none' && !studentDepartmentIds.has(id),
    );
  }, [departmentAccess, studentDepartmentIds]);

  const directDepartments = useMemo(
    () =>
      allDepartmentAccess.filter(
        (dept) =>
          dept.isDirect && !studentDepartmentIds.has(dept.department_id),
      ),
    [allDepartmentAccess, studentDepartmentIds],
  );

  const inheritedDepartments = useMemo(
    () =>
      allDepartmentAccess.filter(
        (dept) =>
          !dept.isDirect &&
          !studentDepartmentIds.has(dept.department_id) &&
          !directDepartments.some(
            (d) => d.department_id === dept.department_id,
          ),
      ),
    [allDepartmentAccess, directDepartments, studentDepartmentIds],
  );

  const icons = useMemo(() => {
    const icons = {} as Record<string, ReactNode>;

    allDepartmentAccess.forEach((dept) => {
      switch (dept.access_level) {
        case DepartmentAccessLevelDTO.editor:
          icons[dept.department_id] = (
            <IconEdit title={formatMessage({ defaultMessage: 'Redigering' })} />
          );
          break;
        case DepartmentAccessLevelDTO['bearer-manager']:
          icons[dept.department_id] = (
            <IconSmartcard
              title={formatMessage({ defaultMessage: 'Bärarhantering' })}
            />
          );
          break;
        default:
          icons[dept.department_id] = (
            <IconEye title={formatMessage({ defaultMessage: 'Läsåtkomst' })} />
          );
      }
    });

    if (isEditMode) {
      selectedNewDepartmentIds.forEach((deptId) => {
        icons[deptId] = <IconCheckCircle className="size-6" />;
      });
    }

    return icons;
  }, [
    allDepartmentAccess,
    formatMessage,
    isEditMode,
    selectedNewDepartmentIds,
  ]);

  const onOrganizationLevelChange = useCallback(
    async (level: DepartmentAccessLevelDTO) => {
      if (
        level === DepartmentAccessLevelDTO.none ||
        level === organizationAccessLevel[administrator.adminType]
      ) {
        return;
      }

      setPendingOrganizationLevel(level);
      try {
        await updateOrganizationUserRole.mutateAsync({
          data: {
            userID: administrator.id,
            type: level,
          },
        });

        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: getListOrganizationUsersQueryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: getGetDepartmentUserWithDepartmentsQueryKey(
              administrator.id,
            ),
          }),
        ]);

        await refreshDepartmentAccess();

        toast.success(
          {
            title: formatMessage({
              defaultMessage: 'Rättigheter uppdaterade',
            }),
          },
          { autoClose: 3000 },
        );
      } catch (error) {
        console.error('Error: ', error);
        toast.error(
          {
            title: formatMessage({
              defaultMessage: 'Ett fel inträffade',
            }),
          },
          { autoClose: 3000 },
        );
      } finally {
        setPendingOrganizationLevel(undefined);
      }
    },
    [
      updateOrganizationUserRole,
      administrator.adminType,
      administrator.id,
      queryClient,
      formatMessage,
    ],
  );

  const onDepartmentLevelChange = useCallback(
    async (departmentId: string, level: DepartmentAccessLevelDTO) => {
      if (level === DepartmentAccessLevelDTO.none) return;

      setPendingDepartmentChange({ departmentId, value: level });
      try {
        await updateDepartmentUser.mutateAsync({
          id: departmentId,
          data: {
            access_level: level,
            userID: administrator.id,
            departmentID: departmentId,
          },
        });

        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: getGetDepartmentUserWithDepartmentsQueryKey(
              administrator.id,
            ),
          }),
          queryClient.invalidateQueries({
            queryKey: getGetCurrentDepartmentTreeQueryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: getGetDepartmentUsersQueryKey(),
          }),
        ]);

        await refreshDepartmentAccess();

        toast.success(
          {
            title: formatMessage({
              defaultMessage: 'Rättigheter uppdaterade',
            }),
          },
          { autoClose: 3000 },
        );
      } catch (error) {
        console.error('Error: ', error);
        toast.error(
          {
            title: formatMessage({
              defaultMessage: 'Ett fel inträffade',
            }),
          },
          { autoClose: 3000 },
        );
      } finally {
        setPendingDepartmentChange(null);
      }
    },
    [updateDepartmentUser, administrator.id, queryClient, formatMessage],
  );

  const onConfirmDeleteDepartmentAccess = useCallback(
    async (departmentId: string) => {
      try {
        await deleteDepartmentUser.mutateAsync({
          departmentID: departmentId,
          userID: administrator.id,
        });

        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: getGetDepartmentUserWithDepartmentsQueryKey(
              administrator.id,
            ),
          }),
          queryClient.invalidateQueries({
            queryKey: getGetUsersForDepartmentQueryKey(departmentId),
          }),
        ]);

        await refreshDepartmentAccess();

        setDeleteDepartmentUserId('');

        toast.success({
          title: formatMessage({
            defaultMessage: 'Administratören har tagits bort',
          }),
        });

        const hasStudentAccess = userWithDepartments?.departments?.some(
          (d) => d.department_id && studentDepartmentIds.has(d.department_id),
        );

        if (
          !administrator.adminType.startsWith('org') &&
          directDepartments.length === 1 &&
          !hasStudentAccess
        ) {
          navigate({
            to: '/$tenantId/administrators',
            params: { tenantId },
          });
        }
      } catch (error) {
        toast.error({
          title: formatMessage({
            defaultMessage: 'Kunde inte ta bort administratören',
          }),
        });
      }
    },
    [
      deleteDepartmentUser,
      administrator.id,
      administrator.adminType,
      queryClient,
      formatMessage,
      directDepartments.length,
      navigate,
      tenantId,
      userWithDepartments?.departments,
      studentDepartmentIds,
    ],
  );

  const onConfirmDeleteOrgAdmin = useCallback(async () => {
    const orgAdminId = administrator.user_profiles.find((profile) =>
      isOrganizationRole(roleIds, profile.role_id),
    )?.id;

    if (!orgAdminId) {
      toast.error({
        title: formatMessage({
          defaultMessage: 'Kunde inte hitta organisationadmin',
        }),
      });
      return;
    }

    try {
      await deleteOrgAdmin.mutateAsync({
        id: orgAdminId,
      });

      await queryClient.invalidateQueries({
        queryKey: getListOrganizationUsersQueryKey(),
      });

      toast.success({
        title: formatMessage({
          defaultMessage: 'Organisationadministratören har tagits bort',
        }),
      });

      if (
        !administrator.departments ||
        administrator.departments.length === 0
      ) {
        navigate({
          to: '/$tenantId/administrators',
          params: { tenantId },
        });
      }
    } catch (error) {
      console.error('Error: ', error);
      toast.error({
        title: formatMessage({
          defaultMessage: 'Kunde inte ta bort administratören',
        }),
      });
    }
  }, [
    administrator.user_profiles,
    administrator.departments,
    formatMessage,
    deleteOrgAdmin,
    queryClient,
    navigate,
    tenantId,
    roleIds,
  ]);

  const handleDepartmentSelect = useCallback(
    (department: SelectedDepartment) => {
      setSelectedNewDepartmentIds((prev) => {
        if (prev.includes(department.id)) {
          return prev.filter((id) => id !== department.id);
        }
        return [...prev, department.id];
      });
    },
    [],
  );

  const handleCancelEditMode = useCallback(() => {
    setIsEditMode(false);
    setSelectedNewDepartmentIds([]);
    setNewDepartmentsAccessLevel(DepartmentAccessLevelDTO.editor);
  }, []);

  const handleConfirmAddDepartments = useCallback(async () => {
    if (selectedNewDepartmentIds.length === 0) {
      return;
    }

    try {
      const invitePromises = selectedNewDepartmentIds.map((departmentId) =>
        inviteUserToDepartment.mutateAsync({
          data: {
            firstname: administrator.firstname || '',
            lastname: administrator.lastname || '',
            email: administrator.email,
            department_id: departmentId,
            access_level: newDepartmentsAccessLevel,
          },
        }),
      );

      await Promise.all(invitePromises);

      const invalidationPromises = [
        ...selectedNewDepartmentIds.map((departmentId) =>
          queryClient.invalidateQueries({
            queryKey: getGetUsersForDepartmentQueryKey(departmentId),
          }),
        ),
        queryClient.invalidateQueries({
          queryKey: getGetDepartmentUsersQueryKey(),
        }),
        queryClient.invalidateQueries({
          queryKey: getGetDepartmentUserWithDepartmentsQueryKey(
            administrator.id,
          ),
        }),
        queryClient.invalidateQueries({
          queryKey: getGetCurrentDepartmentTreeQueryKey(),
        }),
      ];

      await Promise.all(invalidationPromises);
      await refreshDepartmentAccess();

      toast.success(
        {
          title: formatMessage(
            {
              defaultMessage:
                'Administratören har lagts till i {count} {count, plural, one {avdelning} other {avdelningar}}',
            },
            {
              count: selectedNewDepartmentIds.length,
            },
          ),
        },
        { autoClose: 3000 },
      );

      setIsEditMode(false);
      setSelectedNewDepartmentIds([]);
      setNewDepartmentsAccessLevel(DepartmentAccessLevelDTO.editor);
    } catch (error) {
      console.error('Error: ', error);
      toast.error(
        {
          title: formatMessage({
            defaultMessage:
              'Kunde inte lägga till administratören i avdelningarna',
          }),
        },
        { autoClose: 3000 },
      );
    }
  }, [
    selectedNewDepartmentIds,
    inviteUserToDepartment,
    administrator.firstname,
    administrator.lastname,
    administrator.email,
    administrator.id,
    newDepartmentsAccessLevel,
    queryClient,
    formatMessage,
  ]);

  const writeAccessDepartmentIds = useMemo(
    () => getWriteAccessDepartmentIds(),
    [],
  );

  const editModeEnabledDepartmentIds = useMemo(() => {
    if (!isEditingSelf) return writeAccessDepartmentIds;
    if (!departmentAccess?.departments) return [];

    return writeAccessDepartmentIds.filter((deptId) => {
      const dept = departmentAccess.departments[deptId];
      if (!dept?.parent_id) return false;
      return hasDepartmentAccess(dept.parent_id);
    });
  }, [isEditingSelf, writeAccessDepartmentIds, departmentAccess]);

  const existingDepartmentIds = useMemo(
    () => allDepartmentAccess.map((dept) => dept.department_id),
    [allDepartmentAccess],
  );

  const enabledCollectionTypes = useMemo(
    () => organization?.enabled_collection_types ?? [],
    [organization?.enabled_collection_types],
  );

  const handleCategoryAccessChange = useCallback(
    async (departmentId: string, newLevel: DepartmentAccessLevelDTO) => {
      const currentLevel =
        userWithDepartments?.departments?.find(
          (d) => d.department_id === departmentId,
        )?.access_level ?? DepartmentAccessLevelDTO.none;

      if (newLevel === currentLevel) return;

      setPendingCategoryChange({ departmentId, value: newLevel });

      try {
        if (newLevel === DepartmentAccessLevelDTO.none) {
          await deleteDepartmentUser.mutateAsync({
            departmentID: departmentId,
            userID: administrator.id,
          });
        } else if (currentLevel === DepartmentAccessLevelDTO.none) {
          await inviteUserToDepartment.mutateAsync({
            data: {
              firstname: administrator.firstname || '',
              lastname: administrator.lastname || '',
              email: administrator.email,
              department_id: departmentId,
              access_level: newLevel,
            },
          });
        } else {
          await updateDepartmentUser.mutateAsync({
            id: departmentId,
            data: {
              access_level: newLevel,
              userID: administrator.id,
              departmentID: departmentId,
            },
          });
        }

        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: getGetDepartmentUserWithDepartmentsQueryKey(
              administrator.id,
            ),
          }),
          queryClient.invalidateQueries({
            queryKey: getGetCurrentDepartmentTreeQueryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: getGetDepartmentUsersQueryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: getGetUsersForDepartmentQueryKey(departmentId),
          }),
        ]);

        await refreshDepartmentAccess();

        setPendingCategoryChange(null);

        toast.success(
          {
            title: formatMessage({
              defaultMessage: 'Rättigheter uppdaterade',
            }),
          },
          { autoClose: 3000 },
        );
      } catch (error) {
        console.error('Error: ', error);
        setPendingCategoryChange(null);
        toast.error(
          {
            title: formatMessage({
              defaultMessage: 'Ett fel inträffade',
            }),
          },
          { autoClose: 3000 },
        );
      }
    },
    [
      userWithDepartments?.departments,
      inviteUserToDepartment,
      deleteDepartmentUser,
      updateDepartmentUser,
      administrator.id,
      administrator.firstname,
      administrator.lastname,
      administrator.email,
      queryClient,
      formatMessage,
    ],
  );

  const categoryAccess = useMemo(() => {
    if (!departmentTree) return [];

    const isOrgViewer = checkUserPermissions({
      permission: 'organization:viewer',
    });
    const isOrgEditor = checkUserPermissions({
      permission: 'organization:editor',
    });

    return enabledCollectionTypes
      .map((collectionType) => {
        const dept = findDepartmentByCollectionType(
          departmentTree,
          collectionType,
        );
        const deptId = dept?.id;

        if (!isOrgViewer && deptId && !hasDepartmentAccess(deptId)) {
          return null;
        }

        const accessLevel =
          (deptId &&
            userWithDepartments?.departments?.find(
              (d) => d.department_id === deptId,
            )?.access_level) ||
          DepartmentAccessLevelDTO.none;

        const canEdit = deptId
          ? isOrgEditor || hasDepartmentWriteAccess(deptId)
          : false;

        return {
          collectionType: collectionType as StudentCollectionType,
          departmentId: deptId,
          accessLevel,
          canEdit,
        };
      })
      .filter(
        (
          item,
        ): item is {
          collectionType: StudentCollectionType;
          departmentId: string;
          accessLevel: DepartmentAccessLevelDTO;
          canEdit: boolean;
        } => item !== null,
      );
  }, [departmentTree, enabledCollectionTypes, userWithDepartments]);

  const hasStudentCategoryAccess = useMemo(
    () => categoryAccess.some((c) => c.accessLevel !== 'none'),
    [categoryAccess],
  );

  if (isLoadingDepartments) {
    return (
      <FormSection title={<FormattedMessage defaultMessage="Rättigheter" />}>
        <Loading />
      </FormSection>
    );
  }

  return (
    <FormSection title={<FormattedMessage defaultMessage="Rättigheter" />}>
      <div className="flex justify-between">
        <p
          className={cn(
            'flex size-fit items-center rounded-full px-3 py-1',
            adminTypeColor[administrator.adminType],
          )}
        >
          {getAdminTypeTranslation(administrator.adminType)}
        </p>

        {administrator.adminType.startsWith('org') &&
          directDepartments.length !== 0 &&
          checkUserPermissions({ permission: 'organization:editor' }) && (
            <IconButton
              icon={<IconTrash />}
              variant="ghost"
              color="red"
              onClick={() => setShowDeleteOrgAdmin(true)}
              title={formatMessage({
                defaultMessage: 'Ta bort organisationadmin',
              })}
              aria-label={formatMessage({
                defaultMessage: 'Ta bort organisationadmin',
              })}
            />
          )}
      </div>

      {adminTypeText && <p className="text-text-700">{adminTypeText}</p>}

      {organizationLevel &&
        checkUserPermissions({ permission: 'organization:editor' }) && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span
              id="organization-access-level-label"
              className="text-text-700"
            >
              <FormattedMessage defaultMessage="Rättighet i organisationen" />
            </span>
            <AccessLevelToggle
              id="organization-access-level"
              labelledBy="organization-access-level-label"
              value={organizationLevel}
              onChange={onOrganizationLevelChange}
              loadingValue={pendingOrganizationLevel}
              disabled={isEditingSelf && administrator.adminType === 'orgAdmin'}
            />
          </div>
        )}

      {!studentsDisabled && categoryAccess.length > 0 && (
        <div className="mt-6">
          <p className="mb-3 font-medium text-text-900">
            <FormattedMessage defaultMessage="Elevkategorier" />
          </p>
          <div className="flex flex-col gap-3">
            {categoryAccess.map(
              ({ collectionType, departmentId, accessLevel, canEdit }) => (
                <div key={collectionType} className="flex flex-col gap-1">
                  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                    <span
                      id={`category-access-${collectionType}-label`}
                      className="text-text-700"
                    >
                      {formatMessage(COLLECTION_TYPE_LABELS[collectionType])}
                    </span>
                    {canEdit && administrator.email !== userProfile?.email ? (
                      <AccessLevelToggle
                        id={`category-access-${collectionType}`}
                        labelledBy={`category-access-${collectionType}-label`}
                        includeNone
                        value={accessLevel}
                        onChange={(level) => {
                          if (departmentId) {
                            handleCategoryAccessChange(departmentId, level);
                          }
                        }}
                        disabled={!departmentId || isEditingSelf}
                        loadingValue={
                          pendingCategoryChange?.departmentId === departmentId
                            ? pendingCategoryChange.value
                            : undefined
                        }
                      />
                    ) : (
                      <AccessLevelChip accessLevel={accessLevel} />
                    )}
                  </div>
                  {accessLevel !== DepartmentAccessLevelDTO.none && (
                    <p className="text-sm text-text-700">
                      <AccessLevelDescription level={accessLevel} />
                    </p>
                  )}
                </div>
              ),
            )}
          </div>
        </div>
      )}

      {directDepartments.length > 0 && (
        <>
          <p className="mt-10 text-text-700">
            <FormattedMessage
              defaultMessage="Har som avdelningsadmin tillgång till {total} {total, plural, one {avdelning} other {avdelningar}} ({direct} direkt, {inherited} underliggande):"
              values={{
                total: directDepartments.length + inheritedDepartments.length,
                direct: directDepartments.length,
                inherited: inheritedDepartments.length,
              }}
            />
          </p>
          <ul>
            {directDepartments.map((dept) => {
              const nameId = `department-access-${dept.department_id}-label`;

              return (
                <li
                  key={`${dept.department_id}-direct`}
                  className="flex w-full items-center gap-2 border-b border-b-background-200 p-3 pb-2"
                >
                  <Link
                    id={nameId}
                    to="/$tenantId/departments/$departmentId"
                    params={{
                      tenantId,
                      departmentId: dept.department_id,
                      view: 'details',
                    }}
                    className="mr-auto truncate hover:font-bold"
                  >
                    {dept.name}
                  </Link>
                  <AccessLevelToggle
                    id={`department-access-${dept.department_id}`}
                    labelledBy={nameId}
                    compact
                    value={dept.access_level}
                    onChange={(level) =>
                      onDepartmentLevelChange(dept.department_id, level)
                    }
                    loadingValue={
                      pendingDepartmentChange?.departmentId ===
                      dept.department_id
                        ? pendingDepartmentChange.value
                        : undefined
                    }
                    disabled={
                      !hasDepartmentWriteAccess(dept.department_id) ||
                      (isEditingSelf &&
                        dept.access_level === DepartmentAccessLevelDTO.editor)
                    }
                  />
                  {hasDepartmentWriteAccess(dept.department_id) &&
                    userProfile?.email !== administrator.email && (
                      <IconButton
                        icon={<IconTrash />}
                        variant="ghost"
                        color="red"
                        title={formatMessage({
                          defaultMessage: 'Ta bort åtkomst',
                        })}
                        aria-label={formatMessage({
                          defaultMessage: 'Ta bort åtkomst',
                        })}
                        disabled={
                          deleteDepartmentUser.isPending ||
                          !hasDepartmentWriteAccess(dept.department_id)
                        }
                        onClick={() =>
                          setDeleteDepartmentUserId(dept.department_id)
                        }
                      />
                    )}
                </li>
              );
            })}
          </ul>
        </>
      )}

      {hasNonStudentDepartmentAccess &&
        (directDepartments.length > 0 ||
          (!studentsDisabled && hasStudentCategoryAccess)) && (
          <>
            <p className="mt-4 text-text-700">
              <FormattedMessage defaultMessage="Visualiserat i organisationen:" />
            </p>
            {isEditMode && (
              <div className="mb-4 rounded-md border border-primary-300 bg-primary-50 p-4">
                <p className="font-medium text-primary-900">
                  <FormattedMessage defaultMessage="Redigeringsläge aktivt" />
                </p>
                <p className="mt-1 text-primary-800">
                  <FormattedMessage defaultMessage="Klicka på avdelningar för att lägga till dem. Avdelningar som redan är tilldelade kan inte ändras." />
                </p>
                <p className="mt-2 font-medium text-primary-900">
                  <FormattedMessage
                    defaultMessage="{count} {count, plural, one {avdelning vald} other {avdelningar valda}}"
                    values={{ count: selectedNewDepartmentIds.length }}
                  />
                </p>
              </div>
            )}
            <DepartmentTree
              mobileView={true}
              onChange={(department) => {
                if (isEditMode) {
                  if (!existingDepartmentIds.includes(department.id)) {
                    handleDepartmentSelect(department);
                  }
                } else {
                  navigate({
                    to: '/$tenantId/departments/$departmentId/$view',
                    params: {
                      tenantId,
                      departmentId: department.id,
                      view: 'details',
                    },
                  });
                }
              }}
              showFilter={false}
              icons={icons}
              className="!w-full"
              initialSortOption="all"
              disabledDepartments={
                isEditMode ? existingDepartmentIds : undefined
              }
              enabledDepartments={
                isEditMode ? editModeEnabledDepartmentIds : undefined
              }
              dontHighlightSelectedDepartment
            />
            {isDepartmentUser() && !isEditMode && (
              <i className="text-text-700">
                <FormattedMessage defaultMessage="Vänligen notera att detta bara visar de rättigheter som du själv har rättighet att se beorende på roll och avdelningar du har tillgång till. Det är möjligt att administratören har tillgång till mer än vad som visas här." />
              </i>
            )}
          </>
        )}

      {hasNonStudentDepartmentAccess &&
        !administrator.adminType.startsWith('org') &&
        editModeEnabledDepartmentIds.length > 0 &&
        (directDepartments.length > 0 ||
          (!studentsDisabled && hasStudentCategoryAccess)) && (
          <div className="mt-6 flex flex-col gap-4">
            {!isEditMode ? (
              <Button
                startIcon={<IconPlus />}
                variant="outline"
                onClick={() => setIsEditMode(true)}
                className="w-full"
              >
                <FormattedMessage defaultMessage="Lägg till fler avdelningar" />
              </Button>
            ) : (
              <>
                <div className="rounded-md border border-background-200 p-4">
                  <FormElement
                    label={
                      <span id="new-departments-access-level-label">
                        <FormattedMessage defaultMessage="Rättighet" />
                      </span>
                    }
                    input={
                      <div className="flex flex-col gap-2">
                        <AccessLevelToggle
                          id="new-departments-access-level"
                          labelledBy="new-departments-access-level-label"
                          value={newDepartmentsAccessLevel}
                          onChange={(level) => {
                            if (level !== DepartmentAccessLevelDTO.none) {
                              setNewDepartmentsAccessLevel(level);
                            }
                          }}
                        />
                        <p className="text-text-700">
                          <AccessLevelDescription
                            level={newDepartmentsAccessLevel}
                          />
                        </p>
                      </div>
                    }
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    onClick={handleCancelEditMode}
                    className="flex-1"
                  >
                    <FormattedMessage defaultMessage="Avbryt" />
                  </Button>
                  <Button
                    variant="filled"
                    onClick={handleConfirmAddDepartments}
                    disabled={
                      selectedNewDepartmentIds.length === 0 ||
                      inviteUserToDepartment.isPending
                    }
                    loading={inviteUserToDepartment.isPending}
                    className="flex-1"
                    startIcon={<IconPlus />}
                  >
                    <FormattedMessage
                      defaultMessage="Lägg till ({count})"
                      values={{ count: selectedNewDepartmentIds.length }}
                    />
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

      <WarningDialog
        title={formatMessage({
          defaultMessage: 'Ta bort avdelningstillgång',
        })}
        content={
          <FormattedMessage defaultMessage="Är du säker på att du vill ta bort administratörens tillgång till den valda avdelningen?" />
        }
        onCancel={() => setDeleteDepartmentUserId('')}
        onConfirm={() =>
          onConfirmDeleteDepartmentAccess(deleteDepartmentUserId)
        }
        buttonText={formatMessage({
          defaultMessage: 'Ta bort',
        })}
        buttonIcon={<IconTrash />}
        loading={deleteDepartmentUser.isPending}
        open={deleteDepartmentUserId !== ''}
      />

      <WarningDialog
        title={formatMessage({
          defaultMessage: 'Ta bort administratör',
        })}
        content={
          <>
            <p>
              <FormattedMessage defaultMessage="Är du säker på att du vill ta bort administratörens tillgång till organisationen?" />
            </p>

            {directDepartments.length > 0 && (
              <p>
                <FormattedMessage defaultMessage="Tillgång till avdelningar påverkas inte av detta." />
              </p>
            )}
          </>
        }
        onCancel={() => setShowDeleteOrgAdmin(false)}
        onConfirm={onConfirmDeleteOrgAdmin}
        buttonText={formatMessage({
          defaultMessage: 'Ta bort',
        })}
        buttonIcon={<IconTrash />}
        loading={deleteOrgAdmin.isPending}
        open={showDeleteOrgAdmin}
      />
    </FormSection>
  );
};
